import { zodResolver } from '@hookform/resolvers/zod';
import { Check } from 'lucide-react';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useCreateStreamServeProjekt } from '@/adapter/opm-cockpit-backend/useStreamServeProjekte';
import { Button } from '@/components/ui/button';
import { getErrResponseDetail, getOnSaveErrorMessage } from '@/lib/utils';
import {
  DEFAULT_STREAMSERVE_PROJEKT_FORM_VALUES,
  StreamServeProjektFormValues,
  streamServeProjektErstellenSchema,
} from '@/pages/Konfiguration/StreamServe/schemas/streamserveErstellen';
import { ErrorMessage } from '@/pages/VorgangBearbeitung/components';
import { ActionButton } from '@/pages/VorgangBearbeitung/components/ActionButton';

export const CreateStreamServeProjektBox: React.FC = () => {
  const form = useForm<StreamServeProjektFormValues>({
    resolver: zodResolver(streamServeProjektErstellenSchema),
    defaultValues: DEFAULT_STREAMSERVE_PROJEKT_FORM_VALUES,
  });
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = form;

  const [showCreateBox, setShowCreateBox] = useState(false);
  const [err, setErr] = useState<unknown>();

  const createMutation = useCreateStreamServeProjekt();

  const onSubmit = (values: StreamServeProjektFormValues) => {
    createMutation.mutate(values, {
      onSuccess: () => {
        setErr(null);
        setShowCreateBox(false);
        reset();
      },
      onError: (error: unknown) => {
        setErr(error);
      },
    });
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-text-primary">
          StreamServe Projekte
        </h2>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowCreateBox((s) => !s)}
        >
          {showCreateBox ? 'Abbrechen' : 'Neues Projekt anlegen'}
        </Button>
      </div>

      {showCreateBox && (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-3">
          <div className="p-4 border border-table-border rounded-md bg-white grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Projektname
              </span>
              <input
                {...register('projektName')}
                placeholder="z.B. project_ZHP_BW"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <span className="text-xs font-semibold uppercase tracking-wide text-red-700 mt-1 min-h-4">
                {errors.projektName?.message}
              </span>
            </div>

            <div>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Projekt-Version
              </span>
              <input
                type="number"
                min={0}
                {...register('projektVersion', { valueAsNumber: true })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <span className="text-xs font-semibold uppercase tracking-wide text-red-700 mt-1 min-h-4">
                {errors.projektVersion?.message}
              </span>
            </div>
          </div>

          <div className="flex justify-end">
            <ActionButton
              type="submit"
              loading={createMutation.isPending}
              loadingText="Anlegen..."
              normalText="Anlegen"
              icon={Check}
              variant="default"
            />
          </div>
        </form>
      )}
      {err && (
        <div>
          <ErrorMessage
            errorMessage={getErrResponseDetail(err)}
            errorList={getOnSaveErrorMessage(err)}
          />
        </div>
      )}
    </>
  );
};
